import { JSX } from 'react';
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { PagedResponse, Guid } from "@iconics/core";
import { Ontology, OntologyNode2, Solution } from './DataModels';
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, RightButtons, Select, HelperText } from '@iconics/components';
import { ApiConstants } from "@iconics/core";
import { TreeView, ITreeViewItem, ITreeViewItemWithChildren } from "@iconics/components";

class AddOntologyNodeDialog extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private validator = new Validator(appContext().core.validationOptions);
    private cacheKey = 'c92f9864-0c3a-4601-abd7-796102202add';

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            solutions: [],
            solutionId: '',
            ontologies: [],
            ontologyId: '',
            ontologyError: '',
            ontologyNodes: [],
            ontologyNodesTree: [],
            ontologyNodeId: '',
            ontologyNodeError: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.validator.addInput('ontology', () => this.state.ontologyId, i => this.setState({ ontologyError: i })).isRequired();
        this.validator.addInput('ontologyNode', () => this.state.ontologyNodeId, i => this.setState({ ontologyNodeError: i })).isRequired();

        if (this.props.open)
        {
            await this.handleOpen();
        }
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): Promise<void>
    {
        if (!prevProps.open && this.props.open)
        {
            await this.handleOpen();
        }
        else if (prevProps.open && !this.props.open)
        {
            this.handleClose();
        }
    }

    private async handleOpen(): Promise<void>
    {
        this.loading(async () =>
        {
            const solutions = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.get<PagedResponse<Solution[]>>({
                select: Solution,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
                orderBy: 'Name',
            });

            const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<Ontology[]>>({
                select: Ontology,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
                orderBy: 'Name',
            });

            this.setState({
                solutions: solutions.value,
                solutionId: '',
                ontologies: ontologies.value,
                ontologyId: '',
                ontologyError: '',
                ontologyNodes: [],
                ontologyNodesTree: [],
                ontologyNodeId: '',
                ontologyNodeError: '',
                ontologyNodesRefreshToken: Guid.new().toString(),
            });
        });
    }

    private async handleSolutionChange(solutionId: string): Promise<void>
    {
        this.loading(async () =>
        {
            const solution = this.state.solutions.find(i => i.Id === solutionId);
            const ontologyId = solution?.Ontology_ID;
            await this.setStateAsync({ solutionId: solutionId });

            if (ontologyId)
            {
                await this.handleOntologyChange(ontologyId);
            }
        });
    }

    private async handleOntologyChange(ontologyId: string): Promise<void>
    {
        this.loading(async () =>
        {
            const ontologyNodes = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<OntologyNode2[]>>({
                tenancy: appContext().tenantId,
                id: ontologyId,
            });

            await this.setStateAsync({
                ontologyId: ontologyId,
                ontologyNodes: ontologyNodes.value,
                ontologyNodesTree: [],
                ontologyNodeId: '',
                ontologyNodeError: '',
                ontologyNodesRefreshToken: Guid.new().toString(),
            });

            this.validator.validate('ontology');
        });
    }

    private async handleGetOntologyNodesByParent(parentId?: string): Promise<ITreeViewItem[]>
    {
        const nodes = this.state.ontologyNodes
            .filter(i => (i.Parent_ID ?? '') === (parentId ?? ''))
            .map(i => ({
                id: i.Id,
                parentId: i.Parent_ID ?? undefined,
                name: i.Name,
            } satisfies ITreeViewItem));

        return nodes;
    }

    private async handleGetOntologyNodeAncestors(nodeId: string): Promise<string[]>
    {
        return []; // not needed because can only add new ontology nodes so no need to pre-select
    }

    private async handleOntologyNodeChange(ontologyNodeId: string): Promise<void>
    {
        await this.setStateAsync({ ontologyNodeId: ontologyNodeId });
        this.validator.validate('ontologyNode');
    }

    private handleCancel(): void
    {
        this.props.onClose();
    }

    private async handleSave(): Promise<void>
    {
        this.validator.validateAll();
        if (!this.validator.isValid)
        {
            return;
        }

        await this.loading(async () =>
        {
            if (this.state.solutionId)
            {
                try
                {
                    await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.solutions.bySolutionId.post({
                        nodeId: this.props.nodeId,
                        tenancy: appContext().tenantId,
                        solutionId: this.state.solutionId,
                        suppressErrorPopup: true,
                    });
                }
                catch
                {
                    // prevent endpoint bombing if solution is already added to the hierarchy node
                }
            }

            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.byOntologyNodeId.post({
                nodeId: this.props.nodeId,
                tenancy: appContext().tenantId,
                ontologyNodeId: this.state.ontologyNodeId,
            });

            this.props.onSave();
        });
    }

    private handleClose(): void
    {
        this.setState({
            solutions: [],
            solutionId: '',
            ontologies: [],
            ontologyId: '',
            ontologyError: '',
            ontologyNodes: [],
            ontologyNodesTree: [],
            ontologyNodeId: '',
            ontologyNodeError: '',
            ontologyNodesRefreshToken: Guid.new().toString(),
        });
    }

    private get solutionHasOntology(): boolean
    {
        const solution = this.state.solutions.find(i => i.Id === this.state.solutionId);
        return (!!solution?.Ontology_ID);
    }

    public render(): JSX.Element
    {
        return (
            <Dialog
                isOpen={this.props.open}
                onOpenChange={() => this.handleCancel()}
                zIndex={10000}
            >
                <DialogContent>
                    <Card
                        title={this.labels.Platform_Node_AddOntologyNodeTitle}
                        subTitle={this.labels.Platform_Node_AddOntologyNodeSubTitle}
                        details={this.labels.Platform_Node_AddOntologyNodeDetails}
                    >
                        <GridContainer>
                            <GridItem>
                                <Select
                                    label={this.labels.Node_Solution}
                                    placeholder={this.labels.Node_Solution}
                                    items={this.state.solutions.map(i => ({ value: i.Id, label: i.Name }))}
                                    value={this.state.solutionId ? [this.state.solutionId] : []}
                                    onSelect={e => this.handleSolutionChange(e.value)}
                                />
                            </GridItem>
                            <GridItem>
                                <Select
                                    label={this.labels.Node_Ontology}
                                    placeholder={this.labels.Node_Ontology}
                                    items={this.state.ontologies.map(i => ({ value: i.Id, label: i.Name }))}
                                    value={this.state.ontologyId ? [this.state.ontologyId] : []}
                                    onSelect={e => this.handleOntologyChange(e.value)}
                                    invalid={!!this.state.ontologyError}
                                    invalidMessage={this.state.ontologyError}
                                    disabled={this.solutionHasOntology}
                                />
                            </GridItem>
                            <GridItem>
                                {!this.state.ontologyError && this.state.ontologyNodeError &&
                                    <HelperText
                                        error={true}>
                                        {this.state.ontologyNodeError}
                                    </HelperText>
                                }
                            </GridItem>
                            <GridItem>
                                <div style={{
                                    minHeight: '100px',
                                    maxHeight: 'min(300px, calc(100vh - 400px))',
                                    overflowY: 'auto',
                                }}>
                                    <TreeView
                                        refreshToken={this.state.ontologyNodesRefreshToken}
                                        nodes={this.state.ontologyNodesTree}
                                        onNodesChange={i => this.setStateAsync({ ontologyNodesTree: i })}
                                        selectedNodeId={this.state.ontologyNodeId}
                                        onSelectedNodeChange={i => this.setState({ ontologyNodeId: i.id })}
                                        onGetNodes={parentId => this.handleGetOntologyNodesByParent(parentId)}
                                        onGetAncestorNodes={nodeId => this.handleGetOntologyNodeAncestors(nodeId)}
                                    />
                                </div>
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!!this.state.ontologyError || !!this.state.ontologyNodeError}
                            >
                                {this.labels.Platform_CommonAction_Add}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        )
    }
}

export default AddOntologyNodeDialog;

export interface IProps
{
    open: boolean;
    nodeId: number;
    onClose: () => void;
    onSave: () => void;
}

export interface IState
{
    solutions: Solution[];
    solutionId: string;
    ontologies: Ontology[];
    ontologyId: string;
    ontologyError: string;
    ontologyNodes: OntologyNode2[];
    ontologyNodesTree: ITreeViewItemWithChildren[];
    ontologyNodeId: string;
    ontologyNodeError: string;
    ontologyNodesRefreshToken?: string;
}
